import { useEffect } from 'react';

/**
 * Hook for IntersectionObserver
 *
 * @param {Object} ref A React ref
 * @param {Function} callback
 * @param {Object} options An object with an "init" member to options with the observer API (@see https://www.w3.org/TR/intersection-observer/#intersection-observer-init, and other custom options for this hook
 *
 * @example
 * const [isVisible, setIsVisible] = useState(false);
 * const containerRef = useRef(null);
 * const callback = () => setIsVisible(true);
 * useIntersectionObserver(containerRef, callback, {
 *   rootMargin: '0px 0px 500px 0px'
 * });
 * return (
 *   <div ref={containerRef}>
 *     {isVisible && <LazyComponent />}
 *   </div>
 * );
 */
export const useIntersectionObserver = (
  ref,
  callback,
  opts = {
    init: { root: undefined, rootMargin: '0px 0px 0px 0px', threshold: 0 },
    disconnect: true,
    preflightConditionForTriggeringCallback: undefined
  }
) => {
  const defaultOptions = {
    init: { root: undefined, rootMargin: '0px 0px 0px 0px', threshold: 0 },
    disconnect: true,
    preflightConditionForTriggeringCallback: undefined
  };
  const options = { ...defaultOptions, ...opts };
  const cbWrapper = (entries, observer) => {
    if (entries[0].isIntersecting || entries[0].isVisible) {
      callback();
      if (options?.disconnect) {
        observer.disconnect();
      }
    }
  };

  useEffect(() => {
    let observer;
    // If IntersectionObserver, observe and execute cbWrapper.
    // If not supported, just execute callback();
    if (
      'IntersectionObserver' in window &&
      (!options.preflightConditionForTriggeringCallback ||
        options.preflightConditionForTriggeringCallback() === false)
    ) {
      observer = new IntersectionObserver(cbWrapper, options?.init);
      if (ref.current) {
        observer.observe(ref.current);
      }
    } else {
      callback();
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);
};
